import { PopoverClose } from "@radix-ui/react-popover";
import { X } from "lucide-react";
import React, { type RefObject } from "react";
import type { Components } from "react-markdown";
import {
  SOURCE_BADGE_ACTIVE_CLASS,
  SOURCE_BADGE_CLASS,
  SOURCE_BADGE_DISABLED_CLASS,
} from "~/components/SourceBadge";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  makeFlexibleSectionRegex,
  makeWordBoundaryRegex,
} from "./escapeRegExp";

export type HighlightableTerm = { term: string; definition: string };
export type Term = { term: string; definition: string };
export type SectionLink = {
  sectionId: string;
  sectionName: string;
  firstPageId: string;
};
export type BatesLink = {
  batesNumber: string;
  pageId: string;
};

export const unwrapGfmTablesInFences = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;
  let language = "";

  const isGfmTableBlock = (blockLines: string[]) => {
    const trimmed = blockLines.map((l) => l.trim());
    let headerIdx = -1;
    for (let i = 0; i < trimmed.length; i++) {
      const line = trimmed[i];
      if (/\|/.test(line)) {
        headerIdx = i;
        break;
      }
    }
    if (headerIdx === -1) return false;
    if (headerIdx + 1 >= trimmed.length) return false;
    const sep = trimmed[headerIdx + 1];
    if (!/^\|?\s*:?-{3,}\s*(\|\s*:?-{3,}\s*)+\|?$/.test(sep)) return false;
    return true;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
        blockStartIdx = out.length;
        language = (fenceMatch[3] || "").toLowerCase();
        out.push(line);
      } else if (marker === fenceMarker) {
        const blockLines = out.slice(blockStartIdx + 1);
        const shouldUnwrap =
          isGfmTableBlock(blockLines) ||
          language === "table" ||
          language === "markdown";

        if (shouldUnwrap) {
          const keptBefore = out.slice(0, blockStartIdx);
          out.length = 0;
          out.push(...keptBefore, ...blockLines);
        } else {
          out.push(line);
        }

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
        language = "";
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};

/**
 * Convert literal \n sequences (backslash + n) in API responses
 * to actual newline characters for proper Markdown rendering.
 */
export const normalizeLiteralNewlines = (content: string): string => {
  if (!content) return content;
  return content.replace(/\\n/g, "\n");
};

export const unwrapAllCodeBlocks = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);

    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
        blockStartIdx = out.length;
      } else if (marker === fenceMarker) {
        const blockLines = out.slice(blockStartIdx);
        const keptBefore = out.slice(0, blockStartIdx);
        out.length = 0;
        out.push(...keptBefore, ...blockLines);

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};
/**
 * Ensure markdown block-level markers (list items and headings) that appear
 * mid-line get proper newlines inserted before them.
 */
export const ensureMarkdownNewlines = (content: string): string => {
  if (!content) return content;

  let result = content;

  // Insert newline before unordered list markers (- * +)
  result = result.replace(/([.!?:;,)]|(?<!\w)\])(?!\n) ?([-*+] )/g, "$1\n$2");

  // Insert newline before ordered list markers (1. 2. etc.)
  result = result.replace(/([!?\]:;,)])(?!\n) ?(\d+\. )/g, "$1\n$2");
  result = result.replace(/(?<!\d)(\.)(?!\n) ?(\d+\. )/g, "$1\n$2");

  // After closing bold markers (**), insert newline before list markers
  result = result.replace(/(\*\*) ([-*+] )/g, "$1\n$2");
  result = result.replace(/(\*\*) (\d+\. )/g, "$1\n$2");

  result = result.replace(/([^\n]) +(#{1,6} )/g, "$1\n\n$2");

  return result;
};

/** @deprecated Use ensureMarkdownNewlines instead */
export const ensureListItemNewlines = ensureMarkdownNewlines;
/** @deprecated Use ensureMarkdownNewlines instead */
export const ensureHeadingNewlines = ensureMarkdownNewlines;

export const ensureBoldHeadingSpacing = (content: string): string => {
  if (!content) return content;

  let result = content;

  result = result.replace(/(\*\*\w+(?:\s+\w+)+\*\*)([A-Z])/g, "$1\n\n$2");

  result = result.replace(
    /([.!?])[ \t]*(?:\n(?!\n))?[ \t]*((?:-\s*)?\*\*[A-Z][^*]*\*\*)/g,
    "$1\n\n$2"
  );

  result = result.replace(/\n{3,}/g, "\n\n");

  return result;
};

export const TERMS_PROSE_CLASS =
  "prose prose-sm dark:prose-invert font-sans normal-case";

/**
 * Shared React Markdown components to render abbr terms with popovers
 * and handle abbr tags inside inline code while preserving code styling.
 */
export function getTermsMarkdownComponents(
  terms: Term[],
  isReport?: boolean
): Components {
  if (isReport) {
    return {};
  }
  type CustomSpanProps = React.HTMLAttributes<HTMLElement> & {
    "data-term-id"?: string;
  };

  const AbbrWithPopover = ({
    term,
    ...props
  }: CustomSpanProps & { term: Term }) => {
    const [hasOpened, setHasOpened] = React.useState(false);
    return (
      <Popover onOpenChange={(open) => open && setHasOpened(true)}>
        <PopoverTrigger asChild>
          <abbr
            {...props}
            className={`cursor-pointer underline decoration-dotted${props.className ? ` ${props.className}` : ""}`}
          />
        </PopoverTrigger>
        {hasOpened && (
          <PopoverContent
            side="top"
            align="start"
            sideOffset={10}
            className={TERMS_PROSE_CLASS}
          >
            <PopoverClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="inline-flex items-center justify-center border-0 absolute hover:bg-transparent right-0 top-0"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverClose>
            <span className="block text-lg font-semibold m-0 p-0">
              {term.term}
            </span>
            <span className="block text-sm font-normal leading-[20px] mb-0">
              {term.definition}
            </span>
          </PopoverContent>
        )}
      </Popover>
    );
  };

  const Abbr = ({ ...props }: CustomSpanProps) => {
    const termId = props["data-term-id"];
    if (termId !== undefined) {
      const term = terms[Number(termId)];
      if (term) {
        return <AbbrWithPopover term={term} {...props} />;
      }
    }
    return <abbr {...props} />;
  };

  const Code = ({
    children,
    className,
    ...props
  }: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
    className?: string;
    children?: React.ReactNode;
  }) => {
    const toArray = React.Children.toArray(children);
    const rawText = toArray
      .map((c) => (typeof c === "string" ? c : ""))
      .join("");

    const isBlockCode =
      (className && className.includes("language-")) || rawText.includes("\n");
    if (isBlockCode) {
      return (
        <code {...props} className={className}>
          {children}
        </code>
      );
    }

    const decodedText = rawText
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");

    if (!decodedText.includes("<abbr")) {
      return (
        <code {...props} className={className}>
          {children}
        </code>
      );
    }

    const parts: React.ReactNode[] = [];
    const regex = /<abbr\s+data-term-id="(\d+)"\s*>\s*([^<]+?)\s*<\/abbr>/gi;

    let lastIndex = 0;
    for (
      let match = regex.exec(decodedText);
      match;
      match = regex.exec(decodedText)
    ) {
      if (match.index > lastIndex) {
        parts.push(decodedText.slice(lastIndex, match.index));
      }
      const id = match[1];
      const label = match[2];
      parts.push(
        <Abbr key={`${match.index}-${id}`} data-term-id={id}>
          {label}
        </Abbr>
      );
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < decodedText.length) {
      parts.push(decodedText.slice(lastIndex));
    }

    const cleanedParts = parts.map((seg) =>
      typeof seg === "string"
        ? seg
            .replace(/&lt;\/?abbr[^&gt;]*&gt;/gi, "")
            .replace(/<\/?abbr[^>]*>/gi, "")
        : seg
    );

    return (
      <code {...props} className={className}>
        {cleanedParts}
      </code>
    );
  };

  const PageReference = ({
    children,
    className,
    ...props
  }: React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  > & {
    className?: string;
    children?: React.ReactNode;
  }) => {
    const isPageRef =
      className?.includes("page-reference") || "data-page-id" in props;

    if (isPageRef) {
      const text = typeof children === "string" ? children : String(children);
      const numberOnly = text.replace(/[\[\]]/g, "");

      // Check if this is a knowledge base source with unknown IDs
      const pageId = (props as any)["data-page-id"];
      const orgId = (props as any)["data-organization-id"];
      const batesNumber = (props as any)["data-bates-number"];

      // Standalone Bates citations (no pageId/orgId but has batesNumber) are clickable
      const hasBatesNumber = !!batesNumber;
      const isUnknownSource =
        (pageId === "unknown" || orgId === "unknown" || !pageId || !orgId) &&
        !hasBatesNumber;

      return (
        <a
          {...props}
          className={`page-reference ${SOURCE_BADGE_CLASS} ${
            isUnknownSource
              ? SOURCE_BADGE_DISABLED_CLASS
              : SOURCE_BADGE_ACTIVE_CLASS
          }`}
          title={
            isUnknownSource
              ? "This source is from the knowledge base and cannot be viewed"
              : batesNumber
                ? `Ref. ${batesNumber}`
                : undefined
          }
        >
          {numberOnly}
        </a>
      );
    }
    return (
      <a {...props} className={className}>
        {children}
      </a>
    );
  };

  return {
    abbr: Abbr as any,
    code: Code as any,
    a: PageReference as any,
  };
}
export function preFilterTerms(
  content: string,
  terms: Array<HighlightableTerm>
): Array<{ original: HighlightableTerm; originalIndex: number }> {
  if (!content || !terms.length) return [];

  const contentLower = content.toLowerCase();
  const filtered: Array<{
    original: HighlightableTerm;
    originalIndex: number;
  }> = [];

  for (let i = 0; i < terms.length; i++) {
    const termLower = terms[i].term.toLowerCase();
    if (contentLower.includes(termLower)) {
      filtered.push({ original: terms[i], originalIndex: i });
    }
  }

  return filtered;
}

export function highlightTermsInMarkdown(
  content: string,
  terms: Array<HighlightableTerm>
): string {
  if (!content || !terms.length) return content;

  const filtered = preFilterTerms(content, terms);
  if (!filtered.length) return content;

  const lines = content.split("\n");
  let inFence = false;
  let fenceMarker = "";
  const processedLines: string[] = [];

  const replaceInText = (text: string) => {
    if (!text) return text;

    type Seg = {
      type: "code" | "text" | "section-link" | "bates-link";
      value: string;
    };
    const parts: Seg[] = [];
    let i = 0;
    let buffer = "";

    const flushText = () => {
      if (buffer) {
        parts.push({ type: "text", value: buffer });
        buffer = "";
      }
    };

    while (i < text.length) {
      // Check for section-link anchor tags - skip highlighting inside them
      const sectionLinkPrefix = '<a class="section-link"';
      if (text.startsWith(sectionLinkPrefix, i)) {
        const closeTag = "</a>";
        const closeIdx = text.indexOf(closeTag, i);
        if (closeIdx !== -1) {
          flushText();
          const sectionLinkChunk = text.slice(i, closeIdx + closeTag.length);
          parts.push({ type: "section-link", value: sectionLinkChunk });
          i = closeIdx + closeTag.length;
          continue;
        }
      }

      const batesLinkPrefix = '<a class="bates-link"';
      if (text.startsWith(batesLinkPrefix, i)) {
        const closeTag = "</a>";
        const closeIdx = text.indexOf(closeTag, i);
        if (closeIdx !== -1) {
          flushText();
          const batesLinkChunk = text.slice(i, closeIdx + closeTag.length);
          parts.push({ type: "bates-link", value: batesLinkChunk });
          i = closeIdx + closeTag.length;
          continue;
        }
      }

      // Skip page-reference anchor tags (source citations)
      const pageRefPrefix = '<a href="#" data-page-id=';
      if (text.startsWith(pageRefPrefix, i)) {
        const closeTag = "</a>";
        const closeIdx = text.indexOf(closeTag, i);
        if (closeIdx !== -1) {
          flushText();
          const pageRefChunk = text.slice(i, closeIdx + closeTag.length);
          parts.push({ type: "bates-link", value: pageRefChunk });
          i = closeIdx + closeTag.length;
          continue;
        }
      }

      if (text[i] === "`") {
        let j = i;
        while (j < text.length && text[j] === "`") j++;
        const tickCount = j - i;

        let k = j;
        let end = -1;
        while (k < text.length) {
          if (text[k] === "`") {
            let m = k;
            while (m < text.length && text[m] === "`") m++;
            const count = m - k;
            if (count === tickCount) {
              end = m;
              break;
            }
            k = m;
          } else {
            k++;
          }
        }

        if (end !== -1) {
          flushText();
          const codeChunk = text.slice(i, end);
          parts.push({ type: "code", value: codeChunk });
          i = end;
          continue;
        } else {
          buffer += text.slice(i, j);
          i = j;
          continue;
        }
      }

      buffer += text[i];
      i++;
    }
    flushText();

    const result = parts
      .map((seg) => {
        if (
          seg.type === "code" ||
          seg.type === "section-link" ||
          seg.type === "bates-link"
        )
          return seg.value;
        return filtered.reduce((acc, { original, originalIndex }) => {
          const regex = makeWordBoundaryRegex(original.term, "gi");
          return acc.replace(
            regex,
            (match) =>
              `<abbr data-term-id="${originalIndex}">${match
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;")}</abbr>`
          );
        }, seg.value);
      })
      .join("");

    return result;
  };

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[2];
        processedLines.push(line);
        continue;
      } else if (inFence && fenceMatch[2] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
        processedLines.push(line);
        continue;
      }
    }

    if (inFence) {
      processedLines.push(line);
    } else {
      processedLines.push(replaceInText(line));
    }
  }

  return processedLines.join("\n");
}

export function highlightSectionsInMarkdown(
  content: string,
  sections: Array<SectionLink>
): string {
  if (!content || !sections.length) return content;

  const lines = content.split("\n");
  let inFence = false;
  let fenceMarker = "";
  const processedLines: string[] = [];

  const replaceInText = (text: string) => {
    if (!text) return text;

    type Seg = { type: "code" | "text"; value: string };
    const parts: Seg[] = [];
    let i = 0;
    let buffer = "";

    const flushText = () => {
      if (buffer) {
        parts.push({ type: "text", value: buffer });
        buffer = "";
      }
    };

    while (i < text.length) {
      if (text[i] === "`") {
        let j = i;
        while (j < text.length && text[j] === "`") j++;
        const tickCount = j - i;

        let k = j;
        let end = -1;
        while (k < text.length) {
          if (text[k] === "`") {
            let m = k;
            while (m < text.length && text[m] === "`") m++;
            const count = m - k;
            if (count === tickCount) {
              end = m;
              break;
            }
            k = m;
          } else {
            k++;
          }
        }

        if (end !== -1) {
          flushText();
          const codeChunk = text.slice(i, end);
          parts.push({ type: "code", value: codeChunk });
          i = end;
          continue;
        } else {
          buffer += text.slice(i, j);
          i = j;
          continue;
        }
      }

      buffer += text[i];
      i++;
    }
    flushText();

    const sortedSections = [...sections].sort(
      (a, b) => b.sectionName.length - a.sectionName.length
    );

    // Helper to apply section replacement while protecting already-inserted section links
    const applySectionReplacement = (
      text: string,
      section: SectionLink
    ): string => {
      const sectionLinkPattern =
        /<a class="(?:section-link|bates-link)"[^>]*>.*?<\/a>/g;
      const protectedParts: Array<{
        type: "protected" | "text";
        value: string;
      }> = [];
      let lastIndex = 0;
      let match;

      while ((match = sectionLinkPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          protectedParts.push({
            type: "text",
            value: text.slice(lastIndex, match.index),
          });
        }
        protectedParts.push({ type: "protected", value: match[0] });
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        protectedParts.push({ type: "text", value: text.slice(lastIndex) });
      }

      if (protectedParts.length === 0) {
        protectedParts.push({ type: "text", value: text });
      }

      const regex = makeFlexibleSectionRegex(section.sectionName, "gi");
      const escapedSectionId = section.sectionId.replace(/"/g, "&quot;");
      const escapedFirstPageId = section.firstPageId.replace(/"/g, "&quot;");
      return protectedParts
        .map((part) => {
          if (part.type === "protected") return part.value;
          return part.value.replace(
            regex,
            (m) =>
              `<a class="section-link" data-section-id="${escapedSectionId}" data-first-page-id="${escapedFirstPageId}">${m
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;")}</a>`
          );
        })
        .join("");
    };

    const result = parts
      .map((seg) => {
        if (seg.type === "code") return seg.value;
        return sortedSections.reduce(
          (acc, section) => applySectionReplacement(acc, section),
          seg.value
        );
      })
      .join("");

    return result;
  };

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[2];
        processedLines.push(line);
        continue;
      } else if (inFence && fenceMatch[2] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
        processedLines.push(line);
        continue;
      }
    }

    if (inFence) {
      processedLines.push(line);
    } else {
      processedLines.push(replaceInText(line));
    }
  }

  return processedLines.join("\n");
}

export function getSectionLinkMarkdownComponents(
  _sections: SectionLink[],
  onSectionClick?: (sectionId: string, firstPageId: string) => void
): Components {
  type CustomAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    "data-section-id"?: string;
    "data-first-page-id"?: string;
    className?: string;
    children?: React.ReactNode;
  };

  const SectionLinkComponent = ({ children, ...props }: CustomAnchorProps) => {
    const sectionId = props["data-section-id"];
    const firstPageId = props["data-first-page-id"];
    const isSectionLink = props.className?.includes("section-link");

    if (isSectionLink && sectionId && firstPageId) {
      return (
        <a
          {...props}
          className="cursor-pointer underline decoration-dotted"
          onClick={(e) => {
            e.preventDefault();
            onSectionClick?.(sectionId, firstPageId);
          }}
        >
          {children}
        </a>
      );
    }

    return (
      <a {...props} className={props.className}>
        {children}
      </a>
    );
  };

  return {
    a: SectionLinkComponent as any,
  };
}

export function highlightBatesInMarkdown(
  content: string,
  batesLinks: Array<BatesLink>
): string {
  if (!content || !batesLinks.length) return content;

  const batesMap = new Map<string, string>();
  for (const link of batesLinks) {
    batesMap.set(link.batesNumber.toLowerCase(), link.pageId);
  }

  const batesPattern = /\[Bates:\s*([^\]]+)\]/gi;

  const lines = content.split("\n");
  let inFence = false;
  let fenceMarker = "";
  const processedLines: string[] = [];

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[2];
        processedLines.push(line);
        continue;
      } else if (inFence && fenceMatch[2] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
        processedLines.push(line);
        continue;
      }
    }

    if (inFence) {
      processedLines.push(line);
    } else {
      processedLines.push(replaceInText(line));
    }
  }

  return processedLines.join("\n");

  function replaceInText(text: string): string {
    if (!text) return text;

    type Seg = { type: "code" | "text"; value: string };
    const parts: Seg[] = [];
    let i = 0;
    let buffer = "";

    const flushText = () => {
      if (buffer) {
        parts.push({ type: "text", value: buffer });
        buffer = "";
      }
    };

    while (i < text.length) {
      if (text[i] === "`") {
        let j = i;
        while (j < text.length && text[j] === "`") j++;
        const tickCount = j - i;

        let k = j;
        let end = -1;
        while (k < text.length) {
          if (text[k] === "`") {
            let m = k;
            while (m < text.length && text[m] === "`") m++;
            const count = m - k;
            if (count === tickCount) {
              end = m;
              break;
            }
            k = m;
          } else {
            k++;
          }
        }

        if (end !== -1) {
          flushText();
          parts.push({ type: "code", value: text.slice(i, end) });
          i = end;
          continue;
        } else {
          buffer += text.slice(i, j);
          i = j;
          continue;
        }
      }

      buffer += text[i];
      i++;
    }
    flushText();

    return parts
      .map((seg) => {
        if (seg.type === "code") return seg.value;
        return seg.value.replace(
          batesPattern,
          (fullMatch, batesNum: string) => {
            const trimmed = batesNum.trim();
            const pageId = batesMap.get(trimmed.toLowerCase());
            if (!pageId) return fullMatch;
            const escapedPageId = pageId.replace(/"/g, "&quot;");
            return `<a class="bates-link" data-bates-page-id="${escapedPageId}">${fullMatch.replace(/"/g, "&quot;").replace(/'/g, "&#39;")}</a>`;
          }
        );
      })
      .join("");
  }
}

/**
 * Event-delegation hook for source-map and bates-link clicks.
 */
export function useSourceMapDelegation(
  ref: RefObject<HTMLElement | null>,
  onSectionClick?: (sectionId: string, firstPageId: string) => void,
  onBatesClick?: (pageId: string) => void
) {
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as Element;

      const sourceMapLink = target.closest<HTMLElement>("a.source-map-link");
      if (sourceMapLink && el.contains(sourceMapLink)) {
        e.preventDefault();
        const sectionId = sourceMapLink.getAttribute("data-section-id");
        const firstPageId =
          sourceMapLink.getAttribute("data-first-page-id") ?? "";
        if (sectionId) onSectionClick?.(sectionId, firstPageId);
        return;
      }

      const batesLink = target.closest<HTMLElement>("a.bates-link");
      if (batesLink && el.contains(batesLink)) {
        e.preventDefault();
        const pageId = batesLink.getAttribute("data-bates-page-id");
        if (pageId) onBatesClick?.(pageId);
        return;
      }

      const sectionLink = target.closest<HTMLElement>("a.section-link");
      if (sectionLink && el.contains(sectionLink)) {
        e.preventDefault();
        const sectionId = sectionLink.getAttribute("data-section-id");
        const firstPageId =
          sectionLink.getAttribute("data-first-page-id") ?? "";
        if (sectionId) onSectionClick?.(sectionId, firstPageId);
        return;
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [ref, onSectionClick, onBatesClick]);
}

export function getTermsAndSectionsMarkdownComponents(
  isSummary: boolean = false,
  terms: Term[],
  sections: SectionLink[],
  onSectionClick?: (sectionId: string, firstPageId: string) => void
): Components {
  if (!isSummary) {
    return {};
  }
  const termComponents = getTermsMarkdownComponents(terms);
  const sectionComponents = getSectionLinkMarkdownComponents(
    sections,
    onSectionClick
  );

  type CustomAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    "data-section-id"?: string;
    "data-first-page-id"?: string;
    "data-bates-page-id"?: string;
    "data-section-name"?: string;
    className?: string;
    children?: React.ReactNode;
  };

  const CombinedAnchor = ({ children, ...props }: CustomAnchorProps) => {
    const isSourceMapLink = props.className?.includes("source-map-link");
    const isSectionLink =
      !isSourceMapLink && props.className?.includes("section-link");
    const isBatesLink = props.className?.includes("bates-link");
    const isPageRef =
      props.className?.includes("page-reference") || "data-page-id" in props;

    if (isSourceMapLink) {
      const sectionName = (props as any)["data-section-name"];
      return (
        <a
          {...props}
          className="source-map-link"
          title={sectionName || undefined}
          role="button"
        >
          {children}
        </a>
      );
    }

    if (isSectionLink) {
      const SectionLink = sectionComponents.a as React.ComponentType<any>;
      return <SectionLink {...props}>{children}</SectionLink>;
    }

    if (isBatesLink) {
      return (
        <a
          {...props}
          className="bates-link cursor-pointer underline decoration-dotted"
        >
          {children}
        </a>
      );
    }

    if (isPageRef) {
      const PageRef = termComponents.a as React.ComponentType<any>;
      return <PageRef {...props}>{children}</PageRef>;
    }

    return (
      <a {...props} className={props.className}>
        {children}
      </a>
    );
  };

  return {
    ...termComponents,
    a: CombinedAnchor as any,
  };
}

export function highlightSourceMapInMarkdown(
  content: string,
  sourceMap: Record<string, string> | null | undefined,
  sectionLinks: SectionLink[]
): string {
  if (!content) return content;

  const sourcePattern = /\[source:([\d,\s]+)\]/g;

  if (!sourceMap || Object.keys(sourceMap).length === 0) {
    return content.replace(sourcePattern, "");
  }

  const sectionLinkMap = new Map<string, string>();
  const sectionNameMap = new Map<string, string>();
  for (const link of sectionLinks) {
    sectionLinkMap.set(link.sectionId, link.firstPageId);
    sectionNameMap.set(link.sectionId, link.sectionName);
  }

  const lines = content.split("\n");
  let inFence = false;
  let fenceMarker = "";
  const processedLines: string[] = [];

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[2];
        processedLines.push(line);
        continue;
      } else if (inFence && fenceMatch[2] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
        processedLines.push(line);
        continue;
      }
    }

    if (inFence) {
      processedLines.push(line);
    } else {
      processedLines.push(
        line.replace(sourcePattern, (_fullMatch, numbersStr: string) => {
          const numbers = numbersStr
            .split(",")
            .map((n) => n.trim())
            .filter(Boolean);

          const links = numbers
            .map((num) => {
              const sectionId = sourceMap[num];
              if (!sectionId) return null;

              const firstPageId = sectionLinkMap.get(sectionId) ?? "";
              const escapedSectionId = sectionId.replace(/"/g, "&quot;");
              const escapedFirstPageId = firstPageId.replace(/"/g, "&quot;");

              const sectionName = sectionNameMap.get(sectionId) ?? "";
              const escapedSectionName = sectionName
                .replace(/&/g, "&amp;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
              const displayNum = num.replace(/^0+/, "") || "0";

              return `<a class="source-map-link" data-section-id="${escapedSectionId}" data-first-page-id="${escapedFirstPageId}" data-section-name="${escapedSectionName}" title="${escapedSectionName}">${displayNum}</a>`;
            })
            .filter(Boolean);

          return links.join("");
        })
      );
    }
  }

  return processedLines.join("\n");
}

export function highlightTermsAndSectionsInMarkdown(
  content: string,
  terms: Array<HighlightableTerm>,
  sections: Array<SectionLink>,
  batesLinks?: Array<BatesLink>,
  sourceMap?: Record<string, string> | null,
  isSummary?: boolean
): string {
  let result = content;
  if (!isSummary) {
    return result;
  }
  if (batesLinks?.length) {
    result = highlightBatesInMarkdown(result, batesLinks);
  }
  if (sections.length) {
    result = highlightSectionsInMarkdown(result, sections);
  }
  if (terms.length) {
    result = highlightTermsInMarkdown(result, terms);
  }
  if (sourceMap !== undefined) {
    result = highlightSourceMapInMarkdown(result, sourceMap, sections);
  }
  return result;
}
